import React from "react";
import {
  Box,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import BusinessDetailsAccordion from "../../../platform/components/business/business/businessDetailsAccordion";
import BusinessAddressForm, {
  BusinessAddress,
} from "../../../platform/components/business/business/businessAddressForm";
import ContactLinksAccordion from "../../../platform/components/business/business/contactLinksAccordion";

type Props = {
  productId?: string | null;
  form: any;
  setForm: React.Dispatch<React.SetStateAction<any>>;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  afhLicenseNumber: string;
  setAfhLicenseNumber: (value: string) => void;
  afhLicenseError: string;
  clearAfhLicenseError: () => void;
  t: (key: string) => string;
  isFormDisabled: boolean;
  rootClientPath: string;
  selectedBusiness?: any;
  isDirty: boolean;
  categories: any[];
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCheckBoxStateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  tooltipData: {
    url: string;
    displayOnBookingPage: string;
    businessProfileName: string;
  };
  handleSelectChange: (e: any) => void;
  forcePrivateListing: boolean;
  address?: BusinessAddress;
  setAddress: React.Dispatch<React.SetStateAction<BusinessAddress | undefined>>;
  addressValid: boolean;
  addrExpanded: boolean;
  setAddrExpanded: (expanded: boolean) => void;
  addrSubmitAttempted: boolean;
  addrHeaderRef: React.MutableRefObject<HTMLDivElement | null>;
  countries: any[];
  setTouched: (value: boolean) => void;
  setIsDirty: (value: boolean) => void;
  contactForm: any;
  anchorEl: HTMLElement | null;
  setAnchorEl: (value: HTMLElement | null) => void;
  socialList: string[];
  handleAddDynamic: (key: "emails" | "phoneNumbers") => void;
  handleDeleteDynamic: (key: "emails" | "phoneNumbers" | "links", idx: number) => void;
  handleDynamicFieldChange: (key: "emails" | "links", idx: number, value: any) => void;
  handleAddLink: (platform: string) => void;
  handlePhoneNumberChange: (idx: number, digits: string) => void;
  handleFaxNumberChange: (digits: string) => void;
  computedPhoneCode: string;
};

export default function BusinessBaseFormSection(props: Props): JSX.Element {
  const {
    form,
    productId,
    setForm,
    errors,
    setErrors,
    afhLicenseNumber,
    setAfhLicenseNumber,
    afhLicenseError,
    clearAfhLicenseError,
    t,
    isFormDisabled,
    rootClientPath,
    selectedBusiness,
    isDirty,
    categories,
    handleInputChange,
    handleCheckBoxStateChange,
    handleUrlChange,
    tooltipData,
    handleSelectChange,
    forcePrivateListing,
    address,
    setAddress,
    addressValid,
    addrExpanded,
    setAddrExpanded,
    addrSubmitAttempted,
    addrHeaderRef,
    countries,
    setTouched,
    setIsDirty,
    contactForm,
    anchorEl,
    setAnchorEl,
    socialList,
    handleAddDynamic,
    handleDeleteDynamic,
    handleDynamicFieldChange,
    handleAddLink,
    handlePhoneNumberChange,
    handleFaxNumberChange,
    computedPhoneCode,
  } = props;

  return (
    <>
      <BusinessDetailsAccordion
        form={form}
        productId={productId}
        setForm={setForm}
        errors={errors}
        setErrors={setErrors}
        afhLicenseNumber={afhLicenseNumber}
        setAfhLicenseNumber={setAfhLicenseNumber}
        afhLicenseError={afhLicenseError}
        clearAfhLicenseError={clearAfhLicenseError}
        t={t}
        isFormDisabled={isFormDisabled}
        rootClientPath={rootClientPath}
        selectedBusiness={selectedBusiness}
        isDirty={isDirty}
        categories={categories}
        handleInputChange={handleInputChange}
        handleCheckBoxStateChange={handleCheckBoxStateChange}
        handleUrlChange={handleUrlChange}
        tooltipData={tooltipData}
        handleSelectChange={handleSelectChange}
        forcePrivateListing={forcePrivateListing}
        showPublicListingToggle={false}
        showCategoryField={true}
        setIsDirty={setIsDirty}
      />

      <Accordion
        expanded={addrExpanded}
        onChange={(_, open) => setAddrExpanded(open)}
        sx={{ mt: 4, borderRadius: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            ref={addrHeaderRef}
            sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}
          >
            <Typography variant="h6">Location & Address</Typography>
            {addressValid ? (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<CheckCircleRoundedIcon />}
                label="Complete"
              />
            ) : (
              <Chip
                size="small"
                color="error"
                variant="outlined"
                icon={<ErrorOutlineRoundedIcon />}
                label="Address required"
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <Grid item xs={12}>
            <Typography variant="h6">Location</Typography>
            <BusinessAddressForm
              disabled={isFormDisabled}
              businessAddress={address}
              countries={countries}
              setBusinessAddress={(nextAddress) => {
                setAddress(nextAddress);
                setTouched(true);
                setIsDirty(true);
              }}
              setActiveCountry={(country) => {
                const rawCode = country.phone_code?.toString() || "";
                const prefixedCode = rawCode.startsWith("+") ? rawCode : `+${rawCode}`;

                setAddress({
                  ...address,
                  countryId: Number(country.id),
                  stateId: undefined,
                  cityId: undefined,
                });

                setForm((prevForm: any) => {
                  const phoneNumbers = [...(prevForm.phoneNumbers || [])];
                  if (phoneNumbers.length === 0) {
                    phoneNumbers.push({
                      countryCode: prefixedCode,
                      phoneNumber: "",
                    });
                  } else {
                    phoneNumbers[0] = {
                      ...phoneNumbers[0],
                      countryCode: prefixedCode,
                    };
                  }
                  return {
                    ...prevForm,
                    countryId: Number(country.id),
                    phoneNumbers,
                  };
                });

                setTouched(true);
                setIsDirty(true);
              }}
            />
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="body2"
                color={addressValid ? "success.main" : "error.main"}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                {addressValid ? (
                  <>
                    <CheckCircleRoundedIcon fontSize="small" />
                    Address looks good
                  </>
                ) : addrSubmitAttempted || addrExpanded ? (
                  <>
                    <ErrorOutlineRoundedIcon fontSize="small" />
                    Please complete all required address fields
                  </>
                ) : null}
              </Typography>
            </Box>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <ContactLinksAccordion
        form={contactForm}
        errors={errors}
        isFormDisabled={isFormDisabled}
        t={t}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        socialList={socialList}
        handleAddDynamic={handleAddDynamic}
        handleDeleteDynamic={handleDeleteDynamic}
        handleDynamicFieldChange={handleDynamicFieldChange}
        handleAddLink={handleAddLink}
        handlePhoneNumberChange={handlePhoneNumberChange}
        handleFaxNumberChange={handleFaxNumberChange}
        computedPhoneCode={computedPhoneCode}
      />
    </>
  );
}
